import { AppDateTime } from '@macs-frontend/core/utils/datetime';
import { UUID } from '@macs-frontend/core/utils/uuid';
import {
  DictionaryTransport,
  TeamCost,
  TeamCostTeamType,
} from '@macs-frontend/features/company-management/team-cost/team-cost.types';
import { Fee } from '@macs-frontend/features/shared/models/fee.model';
import { Boxed } from 'ngrx-forms';
import { BaseExpense } from './expense';
import { InvoiceItemGroup } from './invoice-item-group.model';
import { BaseInvoice, InvoiceItems, InvoiceProject } from './invoice.model';
import { QuantityType } from './quantity-type.model';
import { BaseSupplier } from './supplier';
import { BaseTask } from './task.model';
import {
  InvoiceItemTransport,
  InvoiceItemTransportForm,
} from '@macs-frontend/features/shared/models/invoice-item-transport.model';
import { BaseProjectTask } from '@macs-frontend/features/shared/models/project-task.model';
import { TaxRate } from '@macs-frontend/features/shared/models/tax.model';

export enum InvoiceItemTypes {
  Task = 'TASK',
  Expense = 'EXPENSE',
  Fee = 'FEE',
  UnratedExpense = 'UNRATED_EXPENSE',
  TeamCost = 'TEAM_COST',
  Transport = 'TRANSPORT',
}

export interface BaseInvoiceItem {
  uuid: UUID;
  amount: null | number;
  spoAmount: number;
  spoQty1: number;
  spoQty2: number;
  billablePrice: null | number;
  name: string;
  description: null | string;
  quantity: number;
  qty1: number;
  qty2: number;
  discount: number | null;
  taxRate: TaxRate | null;
  projectId: null | UUID;
  sequence: number;
  createdAt: AppDateTime;
  tax: number | null;
  type: null | InvoiceItems;
  itemType: null | InvoiceItemTypes;
  partiallySelected: any;
  billablePriceMargin: null | number;
  SPONumbers: string[];
  SPOUnitPrice: number;
  unitPrice: null | number;
  globalFee: boolean;
  feeSources: readonly string[];
  inCalc: boolean;
  isVisibleForCustomer: boolean;
  teamCost: Pick<TeamCost, 'uuid' | 'name'> | null;
}

export interface InvoiceItem extends BaseInvoiceItem {
  project: null | InvoiceProject;
  task: null | BaseTask;
  projectTask: null | BaseProjectTask;
  expense: null | BaseExpense;
  fee: null | Fee;
  teamType: null | TeamCostTeamType;
  transport: null | DictionaryTransport;
  itemTransport: null | InvoiceItemTransport;
  invoice:
    | null
    | (BaseInvoice & {
        supplier: BaseSupplier;
      });
  invoiceGroup: null | Omit<InvoiceItemGroup, 'invoiceItems'>;
  feeSources: string[];
  globalFee: boolean;
  isPinned?: boolean;
  qty1Type: null | QuantityType;
  qty2Type: null | QuantityType;
  amountBeforeDiscount?: number;
  color: string;
  originalInvoiceGroup: null | UUID;
}

export type InvoiceItemForm = Omit<
  InvoiceItem,
  | 'project'
  | 'task'
  | 'projectTask'
  | 'expense'
  | 'fee'
  | 'teamType'
  | 'transport'
  | 'itemTransport'
  | 'invoice'
  | 'qty1Type'
  | 'qty2Type'
  | 'invoiceGroup'
  | 'SPONumbers'
  | 'teamCost'
  | 'taxRate'
> & {
  readonly project: Boxed<InvoiceItem['project']>;
  readonly task: Boxed<BaseTask | null>;
  readonly projectTask: Boxed<BaseProjectTask | null>;
  readonly expense: Boxed<BaseExpense | null>;
  readonly fee: Boxed<Fee | null>;
  readonly teamType: Boxed<TeamCostTeamType | null>;
  readonly transport: Boxed<DictionaryTransport | null>;
  readonly itemTransport: Boxed<InvoiceItemTransportForm | null>;
  readonly invoice: Boxed<InvoiceItem['invoice']>;
  readonly qty1Type: Boxed<InvoiceItem['qty1Type']>;
  readonly qty2Type: Boxed<InvoiceItem['qty2Type']>;
  readonly invoiceGroup: Boxed<InvoiceItem['invoiceGroup']>;
  readonly feeSourcesCount?: number;
  readonly SPONumbers: Boxed<InvoiceItem['SPONumbers']>;
  readonly teamCost: Boxed<InvoiceItem['teamCost']>;
  readonly taxRate: Boxed<InvoiceItem['taxRate']>;
};
